import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Send, Sparkles } from 'lucide-react';
import logo from '../assets/logo_shinysmile.png';
import logoNegative from '../assets/logo_shinysmile_negative.png';
import { theme } from '../styles/theme';
import { useScrollReveal, useStaggeredReveal } from '../hooks/useScrollReveal';

function LandingPage({ onNavigate }) {
  const [showSplash, setShowSplash] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    structure: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success' | 'error', message: string }

  // Scroll reveal hooks
  const [aproposRef, aproposRevealed] = useScrollReveal();
  const [tarifsRef, tarifsRevealed] = useScrollReveal();
  const [contactRef, contactRevealed] = useScrollReveal();
  const [aboutCardsRef, aboutCardsRevealed] = useStaggeredReveal(5, { staggerDelay: 50 });

  // Gestion du Splash Screen
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({ name: '', email: '', phone: '', structure: '', message: '' });
      } else {
        setSubmitStatus({ type: 'error', message: data.message || 'Une erreur est survenue' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({ type: 'error', message: 'Impossible de contacter le serveur. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePricingRequest = () => {
    // Pré-remplir le champ message
    setFormData(prev => ({
      ...prev,
      message: 'Bonjour, je souhaite recevoir votre grille tarifaire complète.'
    }));

    // Attendre que React ait mis à jour l'état avant de scroller
    setTimeout(() => {
      const formElement = document.getElementById('contact-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus sur le premier champ vide du formulaire
        setTimeout(() => {
          const nameInput = document.getElementById('name');
          if (nameInput && !formData.name) {
            nameInput.focus();
          }
        }, 500);
      }
    }, 0);
  };

  return (
    /* Ajout de overflow-x-hidden ici pour supprimer la scrollbar horizontale */
    <div className={theme.classes.page + " relative overflow-x-hidden w-full"}>
      
      {/* Splash Screen Intro */}
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center bg-black transition-opacity duration-1000 ease-in-out ${
          showSplash ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="text-center px-10 relative py-16 max-w-full">
          <div className="premium-gleam-overlay" style={{ overflow: 'hidden' }} />
          <h1 className="text-6xl md:text-8xl font-bold splash-text-shine px-4 relative z-10" style={{ lineHeight: '1.5' }}>
            Shiny<span style={{ color: theme.colors.primary.DEFAULT }}>Smile</span>
          </h1>
        </div>
      </div>

      <style>{`
        @keyframes textShine {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }

        .splash-text-shine {
          background: linear-gradient(
            to right, 
            #fff 20%, 
            ${theme.colors.primary.DEFAULT} 40%, 
            ${theme.colors.primary.light} 50%, 
            ${theme.colors.primary.DEFAULT} 60%, 
            #fff 80%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: textShine 4s linear infinite;
        }

        .premium-gleam-overlay {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          z-index: 20;
          pointer-events: none;
          background: linear-gradient(
            110deg,
            transparent 35%,
            rgba(255, 255, 255, 0.4) 45%,
            rgba(255, 255, 255, 0.6) 50%,
            rgba(255, 255, 255, 0.4) 55%,
            transparent 65%
          );
          mix-blend-mode: overlay;
          animation: premiumGleamMove 2.5s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
        }

        @keyframes premiumGleamMove {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .shimmer-effect {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, ${theme.colors.primary.DEFAULT}30, transparent);
          animation: shimmer 3s infinite;
        }
      `}</style>

      {/* Background Logo */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[150vw] h-[150vw] opacity-[0.25]">
          <img src={logo} alt="" className="w-full h-full object-contain blur-sm" />
        </div>
      </div>

      {/* Content */}
      <div className={`relative z-10 transition-all duration-1000 ${showSplash ? 'blur-xl scale-105' : 'blur-0 scale-100'}`}>
        {/* Header / Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/30 backdrop-blur-xl border-b border-white/[0.07]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <div className="flex items-center space-x-3">
                <img src={logoNegative} alt="ShinySmile" className="h-10 w-auto" />
                <span className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                  Shiny<span style={{ color: theme.colors.primary.DEFAULT }}>Smile</span>
                </span>
              </div>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-8">
                <a href="#apropos" className="text-gray-300 hover:text-white transition-colors">
                  À Propos
                </a>
                <a href="#tarifs" className="text-gray-300 hover:text-white transition-colors">
                  Tarifs
                </a>
                <a href="#contact" className="text-gray-300 hover:text-white transition-colors">
                  Contact
                </a>
                <button
                  onClick={() => onNavigate('login')}
                  className={theme.classes.button.primary}
                >
                  Connexion
                </button>
              </nav>

              {/* Mobile menu button */}
              <div className="md:hidden">
                <button
                  onClick={() => onNavigate('login')}
                  className={theme.classes.button.primary}
                >
                  Connexion
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div
              className="inline-flex items-center space-x-2 rounded-full px-4 py-2 mb-8 relative overflow-hidden"
              style={{
                backgroundColor: `${theme.colors.primary.DEFAULT}10`,
                borderWidth: '1px',
                borderColor: `${theme.colors.primary.DEFAULT}33`
              }}
            >
              <div className="shimmer-effect" />
              <Sparkles className="w-4 h-4 relative z-10" style={{ color: theme.colors.primary.DEFAULT }} />
              <span className="text-sm relative z-10" style={{ color: theme.colors.primary.light }}>Votre laboratoire de prothèses dentaires</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Excellence & Précision
            </h1>

            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">
              Des solutions prothétiques sur-mesure avec un espace client digital pour simplifier vos commandes
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => onNavigate('register')}
                className="px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 transform hover:scale-105 btn-glow"
                style={{
                  backgroundColor: theme.colors.primary.DEFAULT,
                  color: '#000000'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = theme.colors.primary.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = theme.colors.primary.DEFAULT}
              >
                Créer un compte
              </button>
              <a
                href="#contact-form"
                className="border-2 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200"
                style={{
                  borderColor: theme.colors.border.card,
                  color: theme.colors.text.primary
                }}
                onMouseEnter={(e) => e.target.style.borderColor = theme.colors.primary.DEFAULT}
                onMouseLeave={(e) => e.target.style.borderColor = theme.colors.border.card}
              >
                Nous contacter
              </a>
            </div>
          </div>
        </section>

        {/* À Propos Section */}
        <section id="apropos" ref={aproposRef} className={`py-20 px-4 sm:px-6 lg:px-8 scroll-reveal ${aproposRevealed ? 'revealed' : ''}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                À Propos de Nous
              </h2>
            </div>

            <div ref={aboutCardsRef} className="max-w-4xl mx-auto space-y-8">
              <div className={`glass border border-white/10 rounded-2xl p-8 transition-all duration-300 ${aboutCardsRevealed[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Chez Shiny Smile, nous croyons que la précision et la confiance sont essentielles pour créer des prothèses dentaires de qualité.
                  C'est pourquoi <span className="text-white font-semibold">nous accompagnons les laboratoires et praticiens</span> comme un véritable partenaire de production,
                  <span className="text-white font-semibold"> engagé à vos côtés</span> à chaque étape de la conception.
                </p>
              </div>

              <div className={`glass border border-white/10 rounded-2xl p-8 transition-all duration-300 ${aboutCardsRevealed[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Spécialisés dans la conception numérique de prothèses dentaires sur mesure, nous allions expertise technique, sens du détail et innovation digitale pour vous offrir des solutions
                  fiables, esthétiques et parfaitement adaptées à vos prescriptions. Notre objectif est simple : vous faire gagner du temps, sécuriser vos échanges et garantir des résultats maîtrisés.
                </p>
              </div>

              <div className={`glass border border-white/10 rounded-2xl p-8 transition-all duration-300 ${aboutCardsRevealed[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Grâce à notre <span className="text-white font-semibold">plateforme digitale</span> dédiée, le transfert des fichiers et des prescriptions est
                  <span className="text-white font-semibold"> fluide, sécurisé et transparent</span>.
                  Chaque dossier bénéficie d'un suivi structuré, d'un contrôle de faisabilité rigoureux et d'une conception 3D précise, jusqu'à la validation finale du design prêt à produire.
                </p>
              </div>

              <div className={`glass border border-white/10 rounded-2xl p-8 transition-all duration-300 ${aboutCardsRevealed[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Shiny Smile conçoit des prothèses conjointes, adjointes et combinées, avec une attention particulière portée à
                  <span className="text-white font-semibold"> l'esthétique naturelle</span>, à la
                  <span className="text-white font-semibold"> fonctionnalité</span> et au
                  <span className="text-white font-semibold"> respect strict des indications cliniques</span>.
                  Notre expérience en implantologie nous permet de répondre aux cas les plus exigeants, tout en maintenant une réactivité de communication et une grande souplesse de collaboration.
                </p>
              </div>

              <div
                className={`rounded-2xl p-8 text-center transition-all duration-300 ${aboutCardsRevealed[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                style={{
                  backgroundColor: `${theme.colors.primary.DEFAULT}10`,
                  borderWidth: '1px',
                  borderColor: `${theme.colors.primary.DEFAULT}33`
                }}
              >
                <p className="text-gray-300 text-lg leading-relaxed">
                  Animés par la passion du métier et la recherche constante d'excellence, nous faisons évoluer Shiny Smile vers une solution de production complète,
                  toujours plus performante et cohérente.
                </p>
                <p className="text-white text-xl font-semibold mt-4">
                  La précision au service de votre sourire, avec un savoir-faire 100 % Made in France.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tarifs Section */}
        <section id="tarifs" ref={tarifsRef} className={`py-20 px-4 sm:px-6 lg:px-8 scroll-reveal ${tarifsRevealed ? 'revealed' : ''}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Nos Tarifs
              </h2>
              <p className="text-xl text-gray-400">
                Des tarifs compétitifs et transparents
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div
                className="rounded-2xl p-10 text-center"
                style={{
                  backgroundColor: `${theme.colors.primary.DEFAULT}10`,
                  borderWidth: '1px',
                  borderColor: `${theme.colors.primary.DEFAULT}33`
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                  style={{ backgroundColor: `${theme.colors.primary.DEFAULT}33` }}
                >
                  <Mail className="w-8 h-8" style={{ color: theme.colors.primary.DEFAULT }} />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4">
                  Recevez notre grille tarifaire
                </h3>

                <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                  Pour recevoir notre grille tarifaire détaillée, contactez-nous via le formulaire ci-dessous.
                  <br />
                  <span className="text-gray-400">Nous vous l'enverrons dans les meilleurs délais.</span>
                </p>

                <button
                  onClick={handlePricingRequest}
                  className="inline-flex items-center space-x-2 px-8 py-4 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 btn-glow"
                  style={{
                    backgroundColor: theme.colors.primary.DEFAULT,
                    color: '#000000'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = theme.colors.primary.hover}
                  onMouseLeave={(e) => e.target.style.backgroundColor = theme.colors.primary.DEFAULT}
                >
                  <Send className="w-5 h-5" />
                  <span>Demander la grille tarifaire</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" ref={contactRef} className={`py-20 px-4 sm:px-6 lg:px-8 scroll-reveal ${contactRevealed ? 'revealed' : ''}`}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Contactez-nous
              </h2>
              <p className="text-xl text-gray-400">
                Notre équipe est à votre écoute
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-6">
                    Informations de contact
                  </h3>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div
                      className="rounded-lg p-3"
                      style={{ backgroundColor: `${theme.colors.primary.DEFAULT}33` }}
                    >
                      <Mail className="w-6 h-6" style={{ color: theme.colors.primary.DEFAULT }} />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Email</h4>
                      <a
                        href="mailto:contact@shinysmile.fr"
                        className="text-gray-400 transition-colors"
                        onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                        onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                      >
                        contact@shinysmile.fr
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div
                      className="rounded-lg p-3"
                      style={{ backgroundColor: `${theme.colors.primary.DEFAULT}33` }}
                    >
                      <MapPin className="w-6 h-6" style={{ color: theme.colors.primary.DEFAULT }} />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">Adresse</h4>
                      <p className="text-gray-400">
                        34000 Montpellier
                      </p>
                    </div>
                  </div>
                </div>

                {/* Google Maps */}
                <div className="mt-8">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92456.54689796896!2d3.8076890716796875!3d43.61092!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b6af0725dd9db1%3A0xad8756742894e802!2sMontpellier!5e0!3m2!1sfr!2sfr!4v1"
                    width="100%"
                    height="280"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localisation ShinySmile - Montpellier"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div id="contact-form" className="glass border border-white/10 rounded-2xl p-8" style={{ scrollMarginTop: '100px' }}>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Nom/Prénom *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={theme.classes.input.default}
                      placeholder="Dr. Martin Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={theme.classes.input.default}
                      placeholder="contact@cabinet-arceaux.fr"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Numéro de téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={theme.classes.input.default}
                      placeholder="04 67 00 00 00"
                    />
                  </div>

                  <div>
                    <label htmlFor="structure" className="block text-sm font-medium text-gray-300 mb-2">
                      Structure *
                    </label>
                    <select
                      id="structure"
                      name="structure"
                      value={formData.structure}
                      onChange={handleChange}
                      required
                      className={theme.classes.input.default}
                    >
                      <option value="">Sélectionnez une structure</option>
                      <option value="laboratoire">Laboratoire</option>
                      <option value="cabinet">Cabinet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Commentaire
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={theme.classes.input.default + " resize-none"}
                      placeholder="Je souhaite recevoir des informations sur vos services..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: theme.colors.primary.DEFAULT,
                      color: '#000000'
                    }}
                    onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = theme.colors.primary.hover)}
                    onMouseLeave={(e) => e.target.style.backgroundColor = theme.colors.primary.DEFAULT}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Envoyer le message</span>
                      </>
                    )}
                  </button>

                  {submitStatus && (
                    <div
                      className={`mt-4 p-4 rounded-lg text-center ${
                        submitStatus.type === 'success'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black/40 border-t border-white/[0.07] py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <img src={logoNegative} alt="ShinySmile" className="h-8 w-auto" />
                  <span className="text-xl font-bold" style={{ color: theme.colors.text.primary }}>
                    Shiny<span style={{ color: theme.colors.primary.DEFAULT }}>Smile</span>
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  Votre laboratoire de prothèses dentaires avec un espace client moderne pour optimiser vos commandes.
                </p>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-4">Navigation</h4>
                <div className="space-y-2">
                  <a
                    href="#apropos"
                    className="block text-gray-400 transition-colors text-sm"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    À Propos
                  </a>
                  <a
                    href="#tarifs"
                    className="block text-gray-400 transition-colors text-sm"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Tarifs
                  </a>
                  <a
                    href="#contact"
                    className="block text-gray-400 transition-colors text-sm"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Contact
                  </a>
                  <button
                    onClick={() => onNavigate('login')}
                    className="block text-gray-400 transition-colors text-sm"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Espace client
                  </button>
                </div>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-4">Légal</h4>
                <div className="space-y-2 text-gray-400 text-sm">
                  <p
                    className="transition-colors cursor-pointer"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Conditions d'utilisation
                  </p>
                  <p
                    className="transition-colors cursor-pointer"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Politique de confidentialité
                  </p>
                  <p
                    className="transition-colors cursor-pointer"
                    onMouseEnter={(e) => e.target.style.color = theme.colors.primary.DEFAULT}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    Mentions légales
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/[0.07] pt-8 text-center">
              <p className="text-gray-500 text-sm">
                © 2025 ShinySmile. Tous droits réservés.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;